import React, { useMemo } from "react";
import { ItineraryItem, TripLodging, Location, ActivityItem } from "@/types";
import { DateTime } from "luxon";
import { CircleAlert } from "lucide-react";
import { Tooltip } from "@heroui/tooltip";
import { FaExclamationCircle } from "react-icons/fa";
import LazyLoad from "../../image/LazyLoad";
import { formatImageUrl } from "@/lib/general";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import FlightItem from "./FlightItems";
import TravelItem from "./TravelItem";
import LodgingItem from "./LodgingItem";

dayjs.extend(timezone);
dayjs.extend(utc);

// ActionItem component inline definition
interface ActionItemProps {
  action: ItineraryItem;
  locationTimeZone: string;
  dateBeforeArrival: boolean;
}

const ActionItem: React.FC<ActionItemProps> = ({
  action,
  locationTimeZone,
  dateBeforeArrival,
}) => {
  return (
    <div key={action.id} className="activity-item w-full">
      <div className="experience-card flex flex-row w-full items-start transition-transform duration-300 ease-in-out">
        <div className="w-1/6 relative h-[5rem] 2xl:h-[6rem] overflow-hidden rounded-md group">
          <LazyLoad
            src={formatImageUrl(action.images?.[0] || "", "medium")}
            lowResSrc={formatImageUrl(action.images?.[0] || "", "low")}
            alt="Default"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        <div className="flex flex-col w-4/5 pl-3">
          <div className="flex flex-row items-center justify-start space-x-2.5 w-full relative">
            {/* name of experience */}
            <h2 className="text-[15px] 2xl:text-[17px] text-neutral-800 font-medium mb-0.5">
              {truncateTitle(action.name || "", 35)}
            </h2>
            <div className="w-1 h-1 bg-neutral-500 rounded-full transform -translate-y-[1px]"></div>
            {/* start time */}
            <p className="text-neutral-500 font-light text-[11.5px] 2xl:text-[13px] transform -translate-y-[1px]">
              {dayjs(action.start_time).tz(locationTimeZone)?.format("h:mm A")}
            </p>
            {dateBeforeArrival && (
              <div className="absolute top-0 right-0">
                <Tooltip
                  className="shadow-lg ring-1 ring-neutral-200"
                  placement="left"
                  closeDelay={0}
                  showArrow={true}
                  content={
                    <div className="flex flex-row items-center justify-center space-x-2 p-1">
                      <FaExclamationCircle className="text-neutral-800" />
                      <p className="text-sm font-light text-neutral-800">
                        This activity is scheduled before your arrival.
                      </p>
                    </div>
                  }
                >
                  <CircleAlert className="h-4 w-4 2xl:h-5 2xl:w-5 text-rose-500" />
                </Tooltip>
              </div>
            )}
          </div>
          <p className="text-neutral-500 font-light text-[11.5px] 2xl:text-[13px] mt-0.5">
            {reduceExperienceDescription(action.metadata?.description || "")}
          </p>
        </div>
      </div>
    </div>
  );
};

const reduceExperienceDescription = (description: string) => {
  const minLength = 60;

  const sentences = description
    .split(/(?![A-Z][a-z]\.)(?<=\.|\?|!)\s/g)
    .filter((s) => s.trim().length > 0);
  let result = sentences[0] || "";

  if (sentences.length > 1 && result.length < minLength) {
    result += " " + sentences[1];
  }

  result = result.trim();
  return result.length > 0 ? result : description;
};

const truncateTitle = (title: string, maxLength: number) => {
  if (title.length > maxLength) {
    return title.slice(0, maxLength) + "...";
  }
  return title;
};

interface DayItemProps {
  activity: ActivityItem;
  selectedDay: string;
  index: number;
  locationTimeZone: string;
  handleTransportButtonClick: (location: Location) => void;
  nextLocation: Location | null;
  selectedLocation: Location | null;
  previousLocation: Location | null;
  effectiveArrivalDate?: string | null;
  showLocationName?: boolean;
}

const DayItem: React.FC<DayItemProps> = ({
  activity,
  selectedDay,
  index,
  locationTimeZone,
  handleTransportButtonClick,
  nextLocation,
  selectedLocation,
  previousLocation,
  effectiveArrivalDate,
  showLocationName = true,
}) => {
  const isLodging = activity.activityType === "accommodation";
  const isFlight = activity.activityType === "flight";
  const isTravelDay = activity.activityType === "travel-day";
  const isExperience = activity.activityType === "experience";

  const dateBeforeArrival = useMemo(() => {
    return (
      DateTime.fromISO(activity.start_time!).setZone(locationTimeZone) <
      DateTime.fromISO(effectiveArrivalDate!).setZone(locationTimeZone)
    );
  }, [effectiveArrivalDate, locationTimeZone, activity]);

  if (isFlight) {
    return (
      <FlightItem
        flight={activity as FlightItem}
        locationTimeZone={locationTimeZone}
        handleTransportButtonClick={handleTransportButtonClick}
        selectedLocation={selectedLocation!}
        nextLocation={nextLocation!}
      />
    );
  } else if (isTravelDay && previousLocation && selectedLocation) {
    return (
      <TravelItem
        travelDay={activity as ItineraryItem}
        previousLocation={previousLocation}
        selectedLocation={selectedLocation}
      />
    );
  } else if (isExperience) {
    return (
      <ActionItem
        action={activity as ItineraryItem}
        locationTimeZone={locationTimeZone}
        dateBeforeArrival={dateBeforeArrival}
      />
    );
  } else if (isLodging) {
    return (
      <LodgingItem
        lodging={activity as TripLodging}
        locationTimeZone={locationTimeZone}
        locationName={selectedLocation?.name || ""}
        selectedDay={selectedDay}
        showLocationName={showLocationName}
      />
    );
  } else {
    return (
      <div key={index} className="activity-item w-full">
        <h1 className="text-neutral-500">Unknown activity type</h1>
      </div>
    );
  }
};

export default DayItem;
